// src/components/InvoicePrintPreview.tsx
import React, { useMemo } from "react";
import { useData } from "../DataContext";
import { useTranslation } from "react-i18next";

type Props = {
  company?: any;

  invoiceNumber?: string;
  billToName?: string;
  billToAddress?: string;
  issueDate?: string;
  dueDate?: string;

  items: { description: string; qty: number; rate: number }[];

  /** Optional overrides provided by the parent (InvoiceDetailPage). */
  taxAmount?: number; // pass totals.tax
  totalAmount?: number; // pass totals.total
  amountPaid?: number; // total payments applied
  balanceDue?: number; // remaining amount due

  notes?: string;
};

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function InvoicePrintPreview(props: Props) {
  const { t } = useTranslation();
  const data = useData() as any;
  const ctxCompanyInfo = data?.companyInfo;

  const companyInfo = props.company ?? ctxCompanyInfo;

  const subtotal = useMemo(() => {
    return (props.items || []).reduce(
      (sum, it) => sum + (Number(it.qty) || 0) * (Number(it.rate) || 0),
      0
    );
  }, [props.items]);

  // Use passed-in values if provided; otherwise fall back to computed.
  const taxAmount = Number(props.taxAmount ?? 0) || 0;

  const totalAmount =
    props.totalAmount !== undefined
      ? Number(props.totalAmount) || 0
      : subtotal + taxAmount;

  const amountPaid = Number(props.amountPaid ?? 0) || 0;

  const balanceDue =
    props.balanceDue !== undefined
      ? Number(props.balanceDue) || 0
      : Math.max(0, totalAmount - amountPaid);

  const legalName = (companyInfo?.legalName || "").trim();
  const dba = (companyInfo?.dbaName || "").trim();
  const address = (companyInfo?.address || "").trim();
  const phone = (companyInfo?.phone || "").trim();
  const email = (companyInfo?.email || "").trim();
  const website = (companyInfo?.website || "").trim();
  const logoUrl = (companyInfo?.logoUrl || "").trim();
  const taxId = (companyInfo?.taxId || "").trim();
  const licenseNumber = (companyInfo?.licenseNo || "").trim();
  const footerNote = (companyInfo?.notes || "").trim();

  return (
    <div className="invoicePrint" style={{ background: "#fff" }}>
      {/* Optional: print hardening (safe even if you also do it in page CSS) */}
      <style>{`
        @media print {
          .invoicePrint { background: #fff !important; }
          /* Avoid weird page breaks inside the invoice block */
          .invoiceHeader, .invoiceBillTo, .invoiceTable, .invoiceNotes, .invoiceFooter {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="invoiceHeader">
        <div className="invoiceBrand">
          {logoUrl ? (
            <img src={logoUrl} alt="Company logo" className="invoiceLogo" />
          ) : (
            <div className="invoiceLogoFallback">
              {(legalName || "NJ").slice(0, 2).toUpperCase()}
            </div>
          )}

          <div className="invoiceBrandText">
            <div className="invoiceCompanyName">{legalName || "Company name"}</div>
            {dba ? <div className="invoiceMuted">DBA: {dba}</div> : null}
            {address ? <div className="invoiceMuted">{address}</div> : null}

            <div className="invoiceMuted">
              {phone ? <span>{phone}</span> : null}
              {phone && email ? <span> • </span> : null}
              {email ? <span>{email}</span> : null}
              {(phone || email) && website ? <span> • </span> : null}
              {website ? <span>{website}</span> : null}
            </div>

            <div className="invoiceMuted">
              {licenseNumber ? <span>License: {licenseNumber}</span> : null}
              {licenseNumber && taxId ? <span> • </span> : null}
              {taxId ? <span>Tax ID: {taxId}</span> : null}
            </div>
          </div>
        </div>

        <div className="invoiceMeta">
          <div className="invoiceTitle">INVOICE</div>

          <div className="invoiceMetaRow">
            <span className="invoiceMetaKey">Invoice #</span>
            <span className="invoiceMetaVal">{props.invoiceNumber || "—"}</span>
          </div>

          <div className="invoiceMetaRow">
            <span className="invoiceMetaKey">Issue Date</span>
            <span className="invoiceMetaVal">{props.issueDate || "—"}</span>
          </div>

          <div className="invoiceMetaRow">
            <span className="invoiceMetaKey">Due Date</span>
            <span className="invoiceMetaVal">{props.dueDate || "—"}</span>
          </div>
        </div>
      </div>

      <div className="invoiceBillTo">
        <div className="invoiceSectionTitle">Bill To</div>
        <div className="invoiceBillToBox">
          <div style={{ fontWeight: 800 }}>{props.billToName || "—"}</div>
          {props.billToAddress ? (
            <div className="invoiceMuted">{props.billToAddress}</div>
          ) : null}
        </div>
      </div>

      <div className="invoiceTable">
        <div className="invoiceRow invoiceHead">
          <div>{t("invoicePrint.colDescription")}</div>
          <div style={{ textAlign: "right" }}>Qty</div>
          <div style={{ textAlign: "right" }}>Rate</div>
          <div style={{ textAlign: "right" }}>Amount</div>
        </div>

        {(props.items || []).map((it, idx) => {
          const amt = (Number(it.qty) || 0) * (Number(it.rate) || 0);
          return (
            <div className="invoiceRow" key={idx}>
              <div style={{ whiteSpace: "pre-wrap" }}>{it.description}</div>
              <div style={{ textAlign: "right" }}>{it.qty}</div>
              <div style={{ textAlign: "right" }}>{money(it.rate)}</div>
              <div style={{ textAlign: "right", fontWeight: 800 }}>{money(amt)}</div>
            </div>
          );
        })}

        <div className="invoiceTotals">
          <div className="invoiceTotalsRow">
            <span className="invoiceMetaKey">Subtotal</span>
            <span className="invoiceMetaVal">{money(subtotal)}</span>
          </div>

          {taxAmount !== 0 ? (
            <div className="invoiceTotalsRow">
              <span className="invoiceMetaKey">Tax</span>
              <span className="invoiceMetaVal">{money(taxAmount)}</span>
            </div>
          ) : null}

          <div className="invoiceTotalsRow invoiceGrand">
            <span>{t("invoicePrint.colTotal")}</span>
            <span>{money(totalAmount)}</span>
          </div>

          {amountPaid > 0 ? (
            <div className="invoiceTotalsRow">
              <span className="invoiceMetaKey">Amount Paid</span>
              <span className="invoiceMetaVal">{money(amountPaid)}</span>
            </div>
          ) : null}

          {balanceDue > 0 ? (
            <div className="invoiceTotalsRow invoiceGrand">
              <span>{t("invoicePrint.balanceDue")}</span>
              <span>{money(balanceDue)}</span>
            </div>
          ) : null}
        </div>
      </div>

      {props.notes ? (
        <div className="invoiceNotes">
          <div className="invoiceSectionTitle">Notes</div>
          <div className="invoiceMuted" style={{ whiteSpace: "pre-wrap" }}>
            {props.notes}
          </div>
        </div>
      ) : null}

      <div className="invoiceFooter">
        {footerNote ? <div className="invoiceMuted">{footerNote}</div> : null}
        <div className="invoiceMuted">Thank you for your business.</div>
      </div>
    </div>
  );
}
